import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useKioskAuth } from './hooks/useKioskAuth';
import { Loader2, Monitor, AlertCircle } from 'lucide-react';
import KioskSetupAdminCode from './KioskSetupAdminCode';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const CODE_LENGTH = 6;

// ---------------------------------------------------------------------------
// Framer Motion variants
// ---------------------------------------------------------------------------
const PAGE_VARIANTS = {
  initial: { opacity: 0, scale: 0.97 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.97 },
};

const BOX_VARIANTS = {
  idle: {
    borderColor: 'var(--color-slate-300)',
    scale: 1,
  },
  focused: {
    borderColor: 'var(--color-slate-800)',
    scale: 1.05,
  },
  filled: {
    borderColor: 'var(--color-slate-400)',
    scale: 1,
  },
  error: {
    borderColor: 'var(--color-red-500)',
    scale: 1,
  },
};

const CHAR_VARIANTS = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

// ---------------------------------------------------------------------------
// KioskPairingPage
// ---------------------------------------------------------------------------
export default function KioskPairingPage() {
  const navigate = useNavigate();
  const kioskAuth = useKioskAuth();

  const [chars, setChars] = useState(Array(CODE_LENGTH).fill(''));
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shakeKey, setShakeKey] = useState(0);
  const [showAdminSetup, setShowAdminSetup] = useState(false);
  const [pairedToken, setPairedToken] = useState(null);

  const inputRefs = useRef([]);

  // Ref to hold the latest submit function, so handleKeyDown can call it
  // without creating a circular dependency between useCallbacks.
  const submitRef = useRef(null);

  // -----------------------------------------------------------------------
  // If the device is already valid, redirect to the kiosk
  // (Skip redirect if we're showing the admin code setup step)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (kioskAuth.isValid && kioskAuth.deviceToken && !showAdminSetup) {
      navigate(`/kiosk/${kioskAuth.deviceToken}`, { replace: true });
    }
  }, [kioskAuth.isValid, kioskAuth.deviceToken, navigate, showAdminSetup]);

  // -----------------------------------------------------------------------
  // Auto-focus first input on mount
  // -----------------------------------------------------------------------
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  // -----------------------------------------------------------------------
  // Derived state
  // -----------------------------------------------------------------------
  const code = chars.join('');
  const isComplete = code.length === CODE_LENGTH && chars.every((c) => c !== '');

  // -----------------------------------------------------------------------
  // Focus a specific input box
  // -----------------------------------------------------------------------
  const focusInput = useCallback((index) => {
    if (index >= 0 && index < CODE_LENGTH) {
      inputRefs.current[index]?.focus();
      setFocusedIndex(index);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Update a character at a given position
  // -----------------------------------------------------------------------
  const setChar = useCallback(
    (index, value) => {
      setChars((prev) => {
        const next = [...prev];
        next[index] = value.toUpperCase();
        return next;
      });
      setError(null);
    },
    [],
  );

  // -----------------------------------------------------------------------
  // Submit the pairing code
  // -----------------------------------------------------------------------
  const handleSubmit = useCallback(async () => {
    if (!isComplete || isLoading) return;

    setIsLoading(true);
    setError(null);

    const result = await kioskAuth.pair(code);

    if (result.success) {
      // Show admin code setup before navigating to the kiosk
      setPairedToken(result.device_token);
      setShowAdminSetup(true);
      setIsLoading(false);
      return;
    } else {
      setError(result.error || 'Invalid or expired code. Please try again.');
      setShakeKey((prev) => prev + 1);
      setIsLoading(false);

      // Re-focus the first input for correction
      setChars(Array(CODE_LENGTH).fill(''));
      setTimeout(() => focusInput(0), 300);
    }
  }, [isComplete, isLoading, code, kioskAuth, navigate, focusInput]);

  // Keep the ref up to date so handleKeyDown can call it
  submitRef.current = handleSubmit;

  // -----------------------------------------------------------------------
  // Handle input change for each box
  // -----------------------------------------------------------------------
  const handleInput = useCallback(
    (index, e) => {
      const value = e.target.value;

      // If multiple characters were entered (e.g., paste via mobile keyboard),
      // distribute them across boxes.
      if (value.length > 1) {
        const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        setChars((prev) => {
          const newChars = [...prev];
          for (let i = 0; i < cleaned.length && index + i < CODE_LENGTH; i++) {
            newChars[index + i] = cleaned[i];
          }
          return newChars;
        });
        setError(null);

        const nextIndex = Math.min(index + cleaned.length, CODE_LENGTH - 1);
        focusInput(nextIndex);
        return;
      }

      // Single character -- validate it is alphanumeric
      const char = value.toUpperCase();
      if (char && !/^[A-Z0-9]$/.test(char)) return;

      setChar(index, char);

      // Auto-advance to next box
      if (char && index < CODE_LENGTH - 1) {
        focusInput(index + 1);
      }
    },
    [focusInput, setChar],
  );

  // -----------------------------------------------------------------------
  // Handle keydown for navigation
  // -----------------------------------------------------------------------
  const handleKeyDown = useCallback(
    (index, e) => {
      if (e.key === 'Backspace') {
        e.preventDefault();
        if (chars[index]) {
          // Clear current box
          setChar(index, '');
        } else if (index > 0) {
          // Move to previous box and clear it
          setChar(index - 1, '');
          focusInput(index - 1);
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        focusInput(index - 1);
      } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
        e.preventDefault();
        focusInput(index + 1);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        submitRef.current?.();
      }
    },
    [chars, focusInput, setChar],
  );

  // -----------------------------------------------------------------------
  // Handle paste at any box
  // -----------------------------------------------------------------------
  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const pasted = (e.clipboardData.getData('text') || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, CODE_LENGTH);

      if (pasted.length === 0) return;

      const newChars = Array(CODE_LENGTH).fill('');
      for (let i = 0; i < pasted.length; i++) {
        newChars[i] = pasted[i];
      }
      setChars(newChars);
      setError(null);

      const nextIndex = Math.min(pasted.length, CODE_LENGTH - 1);
      focusInput(nextIndex);
    },
    [focusInput],
  );

  // -----------------------------------------------------------------------
  // Determine the animation state for each box
  // -----------------------------------------------------------------------
  const getBoxState = (index) => {
    if (error) return 'error';
    if (focusedIndex === index && document.activeElement === inputRefs.current[index]) return 'focused';
    if (chars[index]) return 'filled';
    return 'idle';
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  // Show admin code setup after successful pairing
  if (showAdminSetup) {
    return (
      <KioskSetupAdminCode
        onComplete={() => {
          navigate(`/kiosk/${pairedToken}`, { replace: true });
        }}
      />
    );
  }

  return (
    <motion.div
      variants={PAGE_VARIANTS}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center px-6 py-12 select-none overflow-hidden"
    >
      <div className="w-full max-w-lg flex flex-col items-center gap-10">
        {/* Icon + Title */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="flex flex-col items-center gap-4 text-center"
        >
          <div className="w-20 h-20 rounded-2xl bg-slate-900 flex items-center justify-center shadow-lg">
            <Monitor className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-800">
            Kiosk Setup
          </h1>
          <p className="text-lg sm:text-xl text-slate-500 max-w-sm leading-relaxed">
            Enter the 6-character pairing code from your Check-In settings page
          </p>
        </motion.div>

        {/* Code input boxes */}
        <motion.div
          key={shakeKey}
          animate={error ? { x: [0, -12, 12, -8, 8, -4, 4, 0] } : {}}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="flex items-center justify-center gap-3 sm:gap-4"
        >
          {Array.from({ length: CODE_LENGTH }).map((_, index) => (
            <motion.div
              key={index}
              variants={BOX_VARIANTS}
              animate={getBoxState(index)}
              transition={{ duration: 0.2 }}
              className="relative"
            >
              <input
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                maxLength={CODE_LENGTH}
                value={chars[index]}
                onChange={(e) => handleInput(index, e)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                onFocus={() => setFocusedIndex(index)}
                disabled={isLoading}
                aria-label={`Pairing code character ${index + 1} of ${CODE_LENGTH}`}
                className={[
                  'w-14 h-16 sm:w-16 sm:h-[4.5rem]',
                  'text-center text-2xl sm:text-3xl font-bold tracking-wide',
                  'border-2 rounded-xl',
                  'bg-white',
                  'outline-none transition-shadow duration-200',
                  'focus:ring-4 focus:ring-slate-200',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  error ? 'text-red-600 border-red-400 bg-red-50' : 'text-slate-800',
                ].join(' ')}
              />

              {/* Character entry animation */}
              <AnimatePresence mode="wait">
                {chars[index] && (
                  <motion.span
                    key={chars[index]}
                    variants={CHAR_VARIANTS}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.15 }}
                    className="absolute inset-0 flex items-center justify-center text-2xl sm:text-3xl font-bold pointer-events-none"
                    aria-hidden="true"
                  >
                    {/* Overlay is purely decorative -- the real character is in the input */}
                  </motion.span>
                )}
              </AnimatePresence>

              {/* Empty state cursor indicator */}
              {!chars[index] && focusedIndex === index && !isLoading && (
                <motion.div
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity, repeatType: 'reverse' }}
                  className="absolute bottom-3 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-slate-400 rounded-full"
                />
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* Error message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -8, height: 0 }}
              transition={{ duration: 0.25 }}
              className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl px-5 py-3"
            >
              <AlertCircle className="w-5 h-5 flex-none" />
              <p className="text-base font-medium">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Connect button */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="w-full max-w-xs"
        >
          <Button
            onClick={handleSubmit}
            disabled={!isComplete || isLoading}
            className="w-full h-16 text-xl font-semibold rounded-2xl shadow-lg transition-all duration-200 hover:shadow-xl active:scale-[0.98] disabled:shadow-none"
          >
            {isLoading ? (
              <span className="flex items-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin" />
                Connecting...
              </span>
            ) : (
              'Connect Kiosk'
            )}
          </Button>
        </motion.div>

        {/* Help text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="text-base text-slate-400 text-center"
        >
          Having trouble? Contact your admin
        </motion.p>
      </div>
    </motion.div>
  );
}
